import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { Feature } from '../../classes/auth/feature.class';
import { Functionality } from '../../classes/auth/functionality';
import { Role } from '../../classes/auth/role.class';
import { CodaltComponent } from '../../codalt.component';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Functionalities } from '../../services/auth/functionalities';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { LocalStorage } from '../../storage.class';

@Component({
    selector: 'app-rights-management',
    standalone: false,
    templateUrl: './rights-management.component.html',
    styleUrl: './rights-management.component.scss',
})
export class RightsManagementComponent extends CodaltComponent implements OnInit {
    disabled = false;
    isProduction = environment.production;

    roles: Role[];
    functionalities: Functionality[];
    features: Feature[];

    search: string;
    highlightedRoleKeys = new Set<string>();
    showOnlyHighlightedRoles = false;
    highlightedFunctionalityKeys = new Set<string>();
    showOnlyHighlightedFunctionalities = false;
    highlightedFunctionalityRowKeys = new Set<string>();
    highlightedFeatureRowKeys = new Set<string>();

    readonly isCodaltUser: boolean = (() => {
        const user = LocalStorage.getUser();
        const email = user?.email?.toLowerCase() || '';
        return email.endsWith('@codalt.com') || AuthorisationService.hasFunctionality(Functionalities.CodaltMode, user);
    })();

    private title = inject(Title);
    private confirmDialogService = inject(ConfirmDialogService);

    constructor(private authorisationService: AuthorisationService) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.authorisationService.getRoles().subscribe(roles => {
                roles.sort((a, b) => {
                    if (a.is_unique !== b.is_unique) {
                        return a.is_unique ? -1 : 1;
                    }

                    return a.name.localeCompare(b.name);
                });

                this.roles = roles;
            })
        );
        this.subscriptions.add(
            this.authorisationService.getFunctionalities().subscribe(functionalities => {
                this.functionalities = functionalities;
            })
        );
        this.subscriptions.add(
            this.authorisationService.getFeatures().subscribe(features => {
                this.features = features;
            })
        );
    }

    checkFunctionality(event: InputEvent, functionality: Functionality, role: Role) {
        if (this.isProduction) {
            event.preventDefault();
            return;
        }

        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this.subscriptions.add(
                this.authorisationService.addFunctionalityToRole(role.key, functionality.key).subscribe(() => {
                    const newFunctionalities = [...(role.functionalities || []), functionality];
                    this.roles = this.roles.map(r =>
                        r.key === role.key ? { ...r, functionalities: newFunctionalities } : r
                    );
                })
            );
        } else {
            this.subscriptions.add(
                this.authorisationService.removeFunctionalityFromRole(role.key, functionality.key).subscribe(() => {
                    const newFunctionalities = (role.functionalities || []).filter(f => f.key !== functionality.key);
                    this.roles = this.roles.map(r =>
                        r.key === role.key ? { ...r, functionalities: newFunctionalities } : r
                    );
                })
            );
        }
    }

    checkFeature(event: InputEvent, functionality: Functionality, feature: Feature) {
        if (this.isProduction) {
            event.preventDefault();
            return;
        }

        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this.subscriptions.add(
                this.authorisationService.addFeatureToFunctionality(functionality.key, feature.key).subscribe(() => {
                    const newFeatures = [...(functionality.features || []), feature];
                    this.functionalities = this.functionalities.map(f =>
                        f.key === functionality.key ? { ...f, features: newFeatures } : f
                    );
                })
            );
        } else {
            this.subscriptions.add(
                this.authorisationService
                    .removeFeatureFromFunctionality(functionality.key, feature.key)
                    .subscribe(() => {
                        const newFeatures = (functionality.features || []).filter(f => f.key !== feature.key);
                        this.functionalities = this.functionalities.map(f =>
                            f.key === functionality.key ? { ...f, features: newFeatures } : f
                        );
                    })
            );
        }
    }

    onFunctionalityRoleDblClick(functionality: Functionality, role: Role) {
        if (!this.isProduction || !this.isCodaltUser) {
            return;
        }

        this.confirmDialogService
            .confirm({
                title: 'Rechtenwijziging op productie',
                text: `<p>Je probeert een rechtenwijziging op productie door te voeren. Dit is niet gewenst. Wil je doorgaan?</p><p class="text-red">Deze actie zal worden gelogd.</p>`,
                cancel: 'Annuleren',
                confirm: 'Doorgaan',
            })
            .then(
                () => {
                    const hasFunctionality = !!role.functionalities?.find(f => f.key === functionality.key);
                    if (hasFunctionality) {
                        this.subscriptions.add(
                            this.authorisationService
                                .removeFunctionalityFromRole(role.key, functionality.key)
                                .subscribe(() => {
                                    const newFunctionalities = (role.functionalities || []).filter(
                                        f => f.key !== functionality.key
                                    );
                                    this.roles = this.roles.map(r =>
                                        r.key === role.key ? { ...r, functionalities: newFunctionalities } : r
                                    );
                                })
                        );
                    } else {
                        this.subscriptions.add(
                            this.authorisationService
                                .addFunctionalityToRole(role.key, functionality.key)
                                .subscribe(() => {
                                    const newFunctionalities = [...(role.functionalities || []), functionality];
                                    this.roles = this.roles.map(r =>
                                        r.key === role.key ? { ...r, functionalities: newFunctionalities } : r
                                    );
                                })
                        );
                    }
                },
                () => {}
            );
    }

    onFeatureFunctionalityDblClick(feature: Feature, functionality: Functionality) {
        if (!this.isProduction || !this.isCodaltUser) {
            return;
        }

        this.confirmDialogService
            .confirm({
                title: 'Rechtenwijziging op productie',
                text: `<p>Je probeert een rechtenwijziging op productie door te voeren. Dit is niet gewenst. Wil je doorgaan?</p><p class="text-red">Deze actie zal worden gelogd.</p>`,
                cancel: 'Annuleren',
                confirm: 'Doorgaan',
            })
            .then(
                () => {
                    const hasFeature = !!functionality.features?.find(f => f.key === feature.key);
                    if (hasFeature) {
                        this.subscriptions.add(
                            this.authorisationService
                                .removeFeatureFromFunctionality(functionality.key, feature.key)
                                .subscribe(() => {
                                    const newFeatures = (functionality.features || []).filter(
                                        f => f.key !== feature.key
                                    );
                                    this.functionalities = this.functionalities.map(f =>
                                        f.key === functionality.key ? { ...f, features: newFeatures } : f
                                    );
                                })
                        );
                    } else {
                        this.subscriptions.add(
                            this.authorisationService
                                .addFeatureToFunctionality(functionality.key, feature.key)
                                .subscribe(() => {
                                    const newFeatures = [...(functionality.features || []), feature];
                                    this.functionalities = this.functionalities.map(f =>
                                        f.key === functionality.key ? { ...f, features: newFeatures } : f
                                    );
                                })
                        );
                    }
                },
                () => {}
            );
    }

    toggleRowHighlight(event: MouseEvent, highlightedKeys: Set<string>, key: string) {
        event.preventDefault();

        if (highlightedKeys.has(key)) {
            highlightedKeys.delete(key);
        } else {
            highlightedKeys.add(key);
        }
    }

    toggleRoleHighlight(event: MouseEvent, role: Role) {
        event.preventDefault();

        if (this.highlightedRoleKeys.has(role.key)) {
            this.highlightedRoleKeys.delete(role.key);
        } else {
            this.highlightedRoleKeys.add(role.key);
        }
    }

    isRoleHighlighted(role: Role): boolean {
        return this.highlightedRoleKeys.has(role.key);
    }

    shouldShowRole(role: Role): boolean {
        return !this.showOnlyHighlightedRoles || this.isRoleHighlighted(role);
    }

    toggleHighlightedRolesVisibility() {
        if (!this.highlightedRoleKeys.size) {
            return;
        }

        this.showOnlyHighlightedRoles = !this.showOnlyHighlightedRoles;
    }

    toggleFunctionalityHighlight(event: MouseEvent, functionality: Functionality) {
        event.preventDefault();

        if (this.highlightedFunctionalityKeys.has(functionality.key)) {
            this.highlightedFunctionalityKeys.delete(functionality.key);
        } else {
            this.highlightedFunctionalityKeys.add(functionality.key);
        }
    }

    isFunctionalityHighlighted(functionality: Functionality): boolean {
        return this.highlightedFunctionalityKeys.has(functionality.key);
    }

    shouldShowFunctionality(functionality: Functionality): boolean {
        return !this.showOnlyHighlightedFunctionalities || this.isFunctionalityHighlighted(functionality);
    }

    toggleHighlightedFunctionalitiesVisibility() {
        if (!this.highlightedFunctionalityKeys.size) {
            return;
        }

        this.showOnlyHighlightedFunctionalities = !this.showOnlyHighlightedFunctionalities;
    }
}
